/**
 * useViewConfig — React hook for the view config registry.
 *
 * Wraps `getOrCreateDefaultViews` with state + setters, persists on every
 * mutation, and exposes a stable `select(items)` helper that every view uses
 * to shape its data.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DatabaseItem } from '../../../types/models/Database';
import type { PropertyFilter } from './calendarConfig';
// F 138 — the F 135+ `ViewLayout` union (10) alongside the legacy `ViewType` (6).
import type { ViewLayout } from '../../../core/views/viewConfig';
import {
  type DatabaseViewConfig,
  type SortDir,
  type SortKey,
  type GroupByMode,
  type ViewType,
  getOrCreateDefaultViews,
  loadViews,
  saveViews,
  selectViewItems,
} from './viewConfig';

/**
 * F 509 — THE GRAPH'S VIEWS, WHEN THEY EXIST.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ MEASURED 2026-11-01 AGAINST PRODUCTION:
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE GRAPH HELD `{ groupBy: "date", groupByField:
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ "prop_1793553268470", groupGranularity: "relative" }
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND THE *LOCALSTORAGE* COPY OF THE SAME VIEW HELD
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `{ groupBy: "status" }` WITH NEITHER `groupByField`
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ NOR `groupGranularity`.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ GRAPH WAS RIGHT AND THE SCREEN WAS WRONG, BECAUSE
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE UI READS `localStorage` AND NOTHING EVER PUSHED
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE GRAPH'S `db.views` BACK INTO IT.**
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE GRAPH'S VIEWS ARE NOW AN INPUT.**
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ WHEN THEY EXIST THEY WIN, BECAUSE THE GRAPH IS
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ WHERE THE DATA LIVES; `localStorage` REMAINS THE
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ FALLBACK FOR A DATABASE THE GRAPH DOES NOT HAVE.
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE VIEW IDs DIFFER BETWEEN THE TWO
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ STORES** — the graph holds `<dbId>_kanban` and the
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ UI mints `view_<dbId>_kanban` — **⇒⇒□ ⇒⇒□ ⇒⇒□
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ SO THE GRAPH'S VIEW KEEPS THE GRAPH'S OWN ID**,
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ because other records point at it.
 */
/**
 * F 509 — ARE THESE THE GRAPH'S *REAL* VIEWS, OR A SYNTHETIC STUB?
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ MEASURED 2026-11-01, BOTH AGAINST THE
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ LIVE GRAPH AND AGAINST THE PRODUCT:**
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A REAL DATABASE'S GRAPH VIEWS CARRY IDS LIKE
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `<dbId>_kanban` AND `<dbId>_list`.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ BUT A *SYNTHETIC* RECORD — THE ONE
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `createDatabase` MANUFACTURES WHEN THE STORE HAS NOT
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ HYDRATED — CARRIES EXACTLY ONE VIEW, WITH THE ID
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `<dbId>_default_view` AND THE TYPE `list`.**
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ AND THAT SINGLE STUB VIEW IS WHAT BROKE
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE F 473 TOOLBAR TEST** — the switcher rendered
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ONE tab ("List") instead of SIX, because the
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ graph's stub was preferred over the localStorage
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ defaults. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE GRAPH'S
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ VIEWS WIN ONLY WHEN THEY ARE REAL** — a stub is
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ not a source of truth about how many views a
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ database has.
 */
function isRealGraphViewList(
  views: readonly DatabaseViewConfig[] | null | undefined,
): views is readonly DatabaseViewConfig[] {
  if (!views || views.length === 0) return false;
  return !views.some((v) => typeof v.id === 'string' && v.id.endsWith('_default_view'));
}

export function useViewConfig(
  databaseId: string,
  graphViews?: readonly DatabaseViewConfig[] | null,
) {
  // Defensive: if databaseId is missing (during navigation, before the page
  // id is ready, or if the DatabaseView is mounted with an undefined id),
  // return safe no-op defaults so we don't write garbage to localStorage or
  // crash downstream components that expect an active view.
  const safe = !databaseId;

  const [views, setViews] = useState<DatabaseViewConfig[]>(() =>
    safe ? [] : (isRealGraphViewList(graphViews)
      ? graphViews.map((v) => ({ ...v }))
      : getOrCreateDefaultViews(databaseId))
  );
  const [activeViewId, setActiveViewIdState] = useState<string>(() => {
    if (safe) return '';
    const initial = isRealGraphViewList(graphViews)
      ? graphViews.map((v) => ({ ...v }))
      : getOrCreateDefaultViews(databaseId);
    // Prefer the last-used view, fall back to the first one
    if (typeof window !== 'undefined') {
      const last = window.localStorage.getItem(`freshcards:activeView:${databaseId}`);
      if (last && initial.some(v => v.id === last)) return last;
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE LAST-USED ID MAY NAME A VIEW IN THE OTHER
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ STORE.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE GRAPH
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ HOLDS `<dbId>_kanban` AND THE UI MINTS
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `view_<dbId>_kanban`, SO AN EXACT MATCH FAILS
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ EVEN THOUGH THE SAME VIEW IS PRESENT.**
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE ACTIVE VIEW IS MATCHED BY
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ *TYPE* WHEN THE EXACT ID IS ABSENT** — the
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ semantic key: "WHICH VIEW IS THIS — THE
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ BOARD, THE LIST, THE GALLERY".
      if (last) {
        const byType = initial.find(
          (v) => (v.type ?? v.layout) === last.split('_').slice(-1)[0],
        );
        if (byType) return byType.id;
      }
    }
    return initial[0]?.id ?? '';
  });

  // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A STABLE SIGNATURE OF THE GRAPH'S VIEWS.** ⇒⇒□
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ IT CHANGES ONLY WHEN THE VIEWS THEMSELVES
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ CHANGE** — the id, the type and the `updatedAt`
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ stamp — so it is a DEPENDENCY THAT MEANS SOMETHING
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ instead of an array identity that changes on every
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ render.
  const graphViewsSignature = useMemo(
    () =>
      (graphViews ?? [])
        .map((v) => `${v.id}:${v.type ?? v.layout ?? ''}:${v.updatedAt ?? 0}`)
        .join('|'),
    [graphViews],
  );

  // Re-load when databaseId changes
  useEffect(() => {
    if (safe) {
      setViews([]);
      setActiveViewIdState('');
      return;
    }
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE GRAPH'S VIEWS WIN OVER THE MINTED
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ DEFAULTS** — the graph is where the data lives,
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ and the defaults would overwrite a real grouping
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ with `groupBy: "status"`.
    const loaded = isRealGraphViewList(graphViews)
      ? graphViews.map((v) => ({ ...v }))
      : getOrCreateDefaultViews(databaseId);
    setViews(loaded);
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE ACTIVE VIEW MUST NOT BE RESET WHEN THE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ LIST IS REPLACED BY A LIST THAT DOES NOT CONTAIN
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ IT.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE ACTIVE VIEW IS
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ A USER CHOICE, AND A RE-HYDRATION MUST NOT
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ SILENTLY THROW IT AWAY.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ AND THE DEPENDENCY IS THE *CONTENT*, NOT THE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ARRAY IDENTITY** — `db.views` IS A NEW ARRAY ON
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ EVERY RENDER, SO DEPENDING ON IT WOULD RE-RUN THE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ EFFECT FOREVER AND THE LIST WOULD NEVER SETTLE.
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THAT WAS MEASURED: THE F 473
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ TOOLBAR TEST WENT RED BECAUSE THE SWITCHER
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ RENDERED ZERO TRIGGERS.**
    setActiveViewIdState((prev) => {
      if (prev && loaded.some((v) => v.id === prev)) return prev;
      return loaded[0]?.id ?? '';
    });
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ AND THE ACTIVE VIEW ID MUST SURVIVE THE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ SWAP** — the old id may be in the localStorage
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ copy while the graph's list uses the graph's own
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ids, so the id is matched BY *TYPE* when the
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ exact id is not present.
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE DEPENDENCY IS THE *CONTENT*, NOT THE ARRAY
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ IDENTITY.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `db.views` IS
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ A NEW ARRAY ON EVERY RENDER**, so depending on it
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ would re-run this effect FOREVER, and `setViews`
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ with a NEW ARRAY every time would re-render the
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ surface forever. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THAT
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ WAS MEASURED: THE F 473 TOOLBAR TEST WENT RED
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ BECAUSE THE SWITCHER RENDERED ZERO TRIGGERS.**
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE EFFECT DEPENDS ON A
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ *SIGNATURE* OF THE VIEWS** — the ids and the
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ updatedAt stamps — which changes only when the
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ views actually change.
  }, [databaseId, safe, graphViewsSignature]);

  /**
   * F 280 — the ONE way this registry mutates, and it is functional.
   *
   * It used to be `persist(next)` taking an already-built array, which forced
   * every caller to build it from the `views` captured in ITS OWN render
   * closure. `createView` did `[...views, view]` and `updateView` did
   * `views.map(...)`, so two mutations inside one batch both started from the
   * same pre-batch array and the second silently discarded the first.
   *
   * That is invisible in the app — a user clicks "New view" in one tick and
   * changes a filter in another — and it is exactly why a test asserting "two
   * views carry two filters" could not pass until this was fixed. Building the
   * next value from the LATEST state is the only version that is correct
   * whether the two changes arrive in one tick or two.
   */
  const commit = useCallback(
    (fn: (prev: DatabaseViewConfig[]) => DatabaseViewConfig[]) => {
      if (safe) return; // don't write garbage to localStorage
      setViews(fn);
    },
    [safe]
  );

  // Persistence lives in an effect watching `views`, NOT inside the updater.
  //
  // React documents updater functions as pure, and this one now has an
  // observable reason to be: the id-collision bug below was only *reproducible*
  // once the write was in the effect, because an effect is where a deferred or
  // discarded updater stops being indistinguishable from a committed one.
  //
  // WHAT I COULD NOT PROVE, recorded so the next reader does not inherit it:
  // an earlier draft of this comment claimed a `table` view "vanished on
  // reload" because of the write living in the updater. It did not. The
  // vanishing was the colliding `Date.now()` id below plus a test that indexed
  // `views[0]` — which is always a minted default. I could not build a
  // failing test that isolates the updater placement (R8 stayed green across
  // three attempts), so I am not claiming it as a bug fix. It is one writer,
  // in one place, and pure updaters.
  useEffect(() => {
    if (safe) return;
    saveViews(databaseId, views);
  }, [views, databaseId, safe]);

  const setActiveViewId = useCallback(
    (id: string) => {
      if (safe) return;
      setActiveViewIdState(id);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(`freshcards:activeView:${databaseId}`, id);
      }
    },
    [databaseId, safe]
  );

  const activeView = useMemo(
    () => views.find(v => v.id === activeViewId) ?? views[0],
    [views, activeViewId]
  );

  const updateView = useCallback(
    (id: string, patch: Partial<DatabaseViewConfig>) => {
      commit((prev) => prev.map(v =>
        v.id === id ? { ...v, ...patch, updatedAt: Date.now() } : v
      ));
    },
    [commit]
  );

  // F 280 - the guard F 138 left behind, and it was costing a real layout.
  //
  // F 138 said "FCC's own surface only RENDERS the original six", so anything
  // outside those six fell back to `list`. That stopped being true in F 141 C,
  // which gave `card`/`table`/`dense`/`detail` a render branch in DatabaseView
  // (:1021, :1033, :1041, :1049) precisely because they rendered NOTHING.
  //
  // So from F 138 until now, choosing "Table view" in the switcher silently
  // created a LIST view: a configuration the user never asked for, stored
  // under the name they typed. The layout is renderable, reachable in the
  // switcher's own "Add a view" menu, and was being downgraded on creation.
  //
  // This is now ALL TEN - typed as `ViewLayout` rather than the legacy
  // six-member `ViewType`, which could not even name four of them.
  const FCC_RENDERABLE: readonly ViewLayout[] = [
    'gallery', 'kanban', 'list', 'calendar', 'timeline', 'recent',   // F 135
    'card', 'table', 'dense', 'detail',                               // F 136
  ];

  const createView = useCallback(
    (requested: ViewLayout, name?: string) => {
      const renderable: ViewLayout = FCC_RENDERABLE.includes(requested)
        ? requested
        : 'list';
      // `type` is the legacy six-member union. The F 136 four have no slot
      // there, so it is only a shape filler for them - `layout` is what
      // `coerceViewConfig` reads FIRST, and it carries the truth.
      const type = (renderable as ViewType) ?? 'list';
      if (safe) return null as unknown as DatabaseViewConfig;
      const now = Date.now();
      // Ids must be unique AND synchronous.
      //
      // `view_<db>_<layout>_<Date.now()>` collides whenever two views of the
      // same layout are created inside one millisecond — and the registry is
      // keyed by id, so the second silently overwrites the first. Minting
      // against the live list would be tidier but cannot be done here: React
      // defers the `setViews` updater, so inside it there is no committed
      // list to test against, and the caller needs the id NOW to make it
      // active.
      //
      // So the id is made unique the way the rest of this file already does it
      // — `coerceViewConfig`'s own fallback is
      // `view_${Math.random().toString(36).slice(2, 10)}`.
      const id = `view_${databaseId}_${renderable}_${now}_${Math.random().toString(36).slice(2, 10)}`;
      const view: DatabaseViewConfig = {
        id,
        databaseId,
        type,
        layout: renderable,
        name: name ?? `${renderable[0].toUpperCase()}${renderable.slice(1)}`,
        sortBy: 'updated',
        sortDir: 'desc',
        createdAt: now,
        updatedAt: now,
      };
      commit((prev) => [...prev, view]);
      setActiveViewId(id);
      return view;
    },
    [commit, databaseId, setActiveViewId, safe]
  );

  const deleteView = useCallback(
    (id: string) => {
      if (safe || views.length <= 1) return; // keep at least one
      commit((prev) => {
        const next = prev.filter(v => v.id !== id);
        if (activeViewId === id && next[0]) setActiveViewId(next[0].id);
        return next;
      });
    },
    [commit, activeViewId, setActiveViewId, safe]
  );

  const renameView = useCallback(
    (id: string, name: string) => updateView(id, { name }),
    [updateView]
  );

  const setSort = useCallback(
    (id: string, sortBy: SortKey, sortDir: SortDir) => updateView(id, { sortBy, sortDir }),
    [updateView]
  );

  const setGroupBy = useCallback(
    (id: string, groupBy: GroupByMode) => updateView(id, { groupBy }),
    [updateView]
  );

  /**
   * F 506 — WRITE THE BOARD'S PRESENTATION TOGGLES.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ MEASURED 2026-11-01, AGAINST LIVE
   * PRODUCTION: THE TOGGLES RENDERED, THE `onClick` WAS ATTACHED TO THE
   * DOM NODE, THE CLICK LANDED — AND THE STATE DID NOT CHANGE.** ⇒⇒□
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ BECAUSE THE HANDLER ONLY WROTE `db.views`
   * (THE GRAPH) AND NEVER `updateView` (THE UI's OWN LIST) — AND
   * `activeView`, WHICH THE BOARD READS, COMES FROM `useViewConfig`.**
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE GRAPH WAS CORRECT AND THE SCREEN
   * WAS WRONG.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ AND THIS IS THE SAME SHAPE AS
   * `setGroupBy` — WHICH DOES BOTH.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A
   * SETTER THAT PERSISTS WITHOUT UPDATING THE UI IS A WRITER WHOSE
   * READER CANNOT SEE IT.**
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND THE PATCH CARRIES ONLY THE KEY THAT
   * CHANGED. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A SETTER THAT WROTE ALL
   * THREE VALUES EVERY TIME WOULD RESET THE OTHER TWO TO WHATEVER THE
   * CALLER THOUGHT THEY WERE.**
   */
  const setBoardOptions = useCallback(
    (
      id: string,
      patch: { wrapContent?: boolean; colorColumns?: boolean; showIcons?: boolean },
    ) => updateView(id, patch as Partial<DatabaseViewConfig>),
    [updateView]
  );

  /**
   * F 509 — WRITE THE DATE GROUPING.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE THIRD AXIS OF THE DATE GROUPING:
   * WHICH PROPERTY, AND HOW COARSELY.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ IT IS A
   * SINGLE SETTER BECAUSE THE TWO ARE ONE DECISION** — A PROPERTY WITHOUT A
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ GRANULARITY IS A HALF-CHOSEN BOARD, AND A GRANULARITY
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ WITHOUT A PROPERTY IS NOT A GROUPING AT ALL.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ AND IT PATCHES ONLY THE KEYS IT IS
   * GIVEN** — FOR THE SAME REASON `setBoardOptions` DOES: A SETTER THAT
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ WROTE EVERY VALUE WOULD RESET THE OTHER ONE TO WHATEVER
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ THE CALLER THOUGHT IT WAS.
   */
  const setDateGrouping = useCallback(
    (
      id: string,
      patch: {
        groupByField?: string;
        groupGranularity?: import('../../../core/views/groupRecords').GroupGranularity;
      },
    ) => updateView(id, patch as Partial<DatabaseViewConfig>),
    [updateView]
  );

  /**
   * F 280 - write a view's filter.
   *
   * Deliberately shaped like `setSort` and `setGroupBy` beside it: id in, patch
   * out, persisted by the same `persist()` that saves every other view change.
   * That symmetry IS the fix - before F 280 the filter was `useState` on
   * DatabaseView, outside this registry entirely, which is why it could not
   * survive a reload and could not differ between two views.
   */
  const setFilter = useCallback(
    (id: string, filter: PropertyFilter) => updateView(id, { filter }),
    [updateView]
  );

  /** The ACTIVE view's filter. Empty object = unfiltered, exactly as before. */
  const activeFilter: PropertyFilter = useMemo(
    () => (activeView?.filter ?? {}) as PropertyFilter,
    [activeView]
  );

  /**
   * Shape `items` for the active view. Memoised on items + activeView.
   * If there's no active view yet (databaseId not ready, views still loading),
   * pass the items through unchanged.
   */
  const select = useCallback(
    (items: DatabaseItem[]) => {
      if (!activeView) return { items, totalCount: items.length };
      return selectViewItems({ items, config: activeView });
    },
    [activeView]
  );

  return {
    views,
    activeView,
    setActiveViewId,
    updateView,
    createView,
    deleteView,
    renameView,
    setSort,
    setGroupBy,
    setBoardOptions,
    setDateGrouping,
    setFilter,
    activeFilter,
    select,
  };
}
